import { useEffect, useState, type FormEvent } from "react";
import { apiFetch } from "../../api/client";

/**
 * «Завершить сессию» — сохранить урок и выйти в кабинет. Не путать с
 * «Завершить занятие» школы: тот отчитывается сайту школы, этот просто
 * подводит черту для себя.
 *
 * Что делает по шагам:
 * 1. ждёт, пока последние правки уйдут на сервер (иначе последний штрих
 *    мог бы потеряться при уходе);
 * 2. меняет название и описание доски, если их поправили;
 * 3. на тарифах со снимками — кладёт снимок «название · дата», чтобы к этому
 *    уроку можно было вернуться, даже если доску потом продолжат;
 * 4. уводит в кабинет.
 */
export type BoardEndSessionDialogProps = {
  boardId: string;
  title: string;
  description: string;
  /** Снимки доступны на тарифе — тогда предлагаем сохранить снимок урока. */
  canSnapshot: boolean;
  /** Дождаться отправки правок; false — не успели. */
  waitSaved: () => Promise<boolean>;
  onSaved: (next: { title: string; description: string }) => void;
  onDone: () => void;
  onClose: () => void;
};

function todayLabel(): string {
  return new Date().toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export function BoardEndSessionDialog({
  boardId,
  title,
  description,
  canSnapshot,
  waitSaved,
  onSaved,
  onDone,
  onClose,
}: BoardEndSessionDialogProps) {
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftDescription, setDraftDescription] = useState(description);
  const [snapshot, setSnapshot] = useState(canSnapshot);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !busy) onClose();
      // Горячие клавиши доски не должны срабатывать из поля ввода.
      ev.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [busy, onClose]);

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (busy) return;
    const nextTitle = draftTitle.trim() || "Доска";
    const nextDescription = draftDescription.trim();
    setBusy(true);
    setError("");
    try {
      setStep("Сохраняю доску…");
      const saved = await waitSaved();
      if (!saved) {
        throw new Error(
          "Не все правки успели уйти на сервер — похоже, пропала связь. Подождите и нажмите ещё раз."
        );
      }
      if (nextTitle !== title || nextDescription !== description) {
        setStep("Сохраняю название…");
        await apiFetch(`/api/v1/boards/${boardId}`, {
          method: "PATCH",
          json: { title: nextTitle, description: nextDescription },
        });
        onSaved({ title: nextTitle, description: nextDescription });
      }
      if (canSnapshot && snapshot) {
        setStep("Сохраняю снимок урока…");
        await apiFetch(`/api/v1/boards/${encodeURIComponent(boardId)}/checkpoints`, {
          method: "POST",
          json: { title: `${nextTitle} · ${todayLabel()}`.slice(0, 120) },
        });
      }
      onDone();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : "Не удалось сохранить");
      setBusy(false);
      setStep("");
    }
  }

  return (
    <dialog className="wb-modal wb-modal--center wb-end-session" open aria-label="Завершить сессию">
      <div className="wb-modal__box">
        <h2 className="wb-modal__heading">Завершить сессию</h2>
        <p className="wb-modal__note wb-end-session__lead">
          Доска сохранится, и вы вернётесь в кабинет. Можно поправить название и оставить
          пару слов о том, что было на уроке.
        </p>
        <form onSubmit={onSubmit}>
          <label className="wb-label" htmlFor="end-session-title">
            Название доски
          </label>
          <input
            id="end-session-title"
            className="wb-input"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            maxLength={255}
            required
            autoFocus
            disabled={busy}
          />
          <label className="wb-label" htmlFor="end-session-description">
            Краткое описание
          </label>
          <textarea
            id="end-session-description"
            className="wb-input wb-textarea"
            value={draftDescription}
            onChange={(e) => setDraftDescription(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Например: квадратные уравнения, разобрали дискриминант, на дом № 5–9"
            disabled={busy}
          />
          {canSnapshot ? (
            <label className="wb-end-session__check">
              <input
                type="checkbox"
                checked={snapshot}
                onChange={(e) => setSnapshot(e.target.checked)}
                disabled={busy}
              />
              <span>
                Сохранить снимок урока — к нему можно будет вернуться в «Снимках», даже если доску
                продолжите
              </span>
            </label>
          ) : null}
          {error ? (
            <p className="wb-modal__note wb-end-session__error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="wb-modal__footer">
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--ghost"
              onClick={onClose}
              disabled={busy}
            >
              Остаться на доске
            </button>
            <button
              type="submit"
              className="wb-modal__btn wb-modal__btn--primary"
              disabled={busy}
            >
              {busy ? step || "Сохраняю…" : "Сохранить и выйти"}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
